import { Intersection } from "../Intersection.js";
import { ORIENTATIONS } from "../../../constants/intxConstants.js";
import { errDiv } from "../../../util/mathHelpers.ts";

// const DEBUG = true;

/** Restricted Crossing U-Turn computational class. Extends the Intersection parent class */
export class RCUT extends Intersection {
  /**
   * Constructor for the RCUT class.
   * @param {string} name - Name of the intersection
   * @param {Object} volumes - Object mapping direction strings (eastbound, westbound, northbound, southbound) to  {@link Volume} objects.
   * @param {Object} globalParams - Object containing the required global parameters for an intersection.
   * @param {string} majorStDirection - Optional string identifier for the major street direction, default = ORIENTATIONS.NORTH_SOUTH
   */
  constructor(
    name,
    volumes,
    globalParams,
    majorStDirection = ORIENTATIONS.NORTH_SOUTH
  ) {
    super(name, volumes, globalParams);

    this.majorStDirection = majorStDirection;

    this.LaneConfig = {
      Z1: {
        Major1: { T: 1 },
        Major2: { UT: 1 },
      },
      Z2: {
        Major1: { UT: 1 },
        Major2: { T: 1 },
      },
      Z3: {
        Minor2: { RT: 1, RTChan: false },
        Major2: {
          T: 1,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
        Major1: { LT: 1 },
      },
      Z4: {
        Minor1: { RT: 1, RTChan: false },
        Major1: {
          T: 1,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
        Major2: { LT: 1 },
      },
    };
  }

  /**
   * Function to get the DEFAULT inputs available for the intersection.  This function is designed to facilitate the
   * integration of the engine into a user interface.
   *
   * A restricted crossing U-Turn (RCUT) has four zones (Z1-Z4) and is dependent on Major/Minor street designation.
   *
   * @return {Object} Object representation of default inputs
   */
  static getZoneDefaultInputs() {
    return {
      Z1: {
        Major1: { T: 1 },
        Major2: { UT: 1 },
      },
      Z2: {
        Major1: { UT: 1 },
        Major2: { T: 1 },
      },
      Z3: {
        Minor2: { RT: 1, RTChan: false },
        Major2: {
          T: 1,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
        Major1: { LT: 1 },
      },
      Z4: {
        Minor1: { RT: 1, RTChan: false },
        Major1: {
          T: 1,
          RT: 1,
          RTShared: false,
          RTChan: false,
        },
        Major2: { LT: 1 },
      },
    };
  }

  static getLaneConfigLabelsMapping(orientation) {
    const isEW = orientation === ORIENTATIONS.EAST_WEST;
    return {
      Z1: this.getIntersectionLabelsMappingWithMajorDir(isEW),
      Z2: this.getIntersectionLabelsMappingWithMajorDir(isEW),
      Z3: this.getIntersectionLabelsMappingWithMajorDir(isEW),
      Z4: this.getIntersectionLabelsMappingWithMajorDir(isEW),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    this.LaneConfig = JSON.parse(JSON.stringify(laneConfigInputs));
  }

  // Implements the computeVCAnalysis function of the Intersection parent class.
  _runCriticalMovementAnalysis() {
    const volumes = this.generateDirectionalVolumes(this.majorStDirection);
    this._unsigUTurnVCParams = {}; // stores all the inputs that goes into the unsig VC helper
    this.revertToSignalized = false; // true if the lane configuration is invalid for unsignalized V/C calc
    // Helper object for Zone 1
    const intx1 = {
      Major1: {
        LT: volumes.Major1.LT,
        T: volumes.Major1.T,
        RT: volumes.Major1.RT,
      },
      Major2: {
        UT: volumes.Minor2.T + volumes.Minor2.LT,
      },
      CLV: -1,
      VC: -1,
    };

    // Determine Z1 Critical Lane Volume and V/C Ratio
    const Z1Maj1LaneVol = errDiv(
      intx1.Major1.LT + intx1.Major1.T + intx1.Major1.RT,
      this.LaneConfig.Z1.Major1.T
    );
    const Z1Maj2ULaneVol = errDiv(
      intx1.Major2.UT,
      this.UTAF,
      this.LaneConfig.Z1.Major2.UT
    );
    intx1.CLV = Z1Maj1LaneVol + Z1Maj2ULaneVol;
    intx1.VC = intx1.CLV / this.CLV_Limit_2;

    // Print statements for debugging
    // if (DEBUG) {
    //   console.log("------- ZONE 1-------");
    //   console.log(
    //     "Z1 Maj1 L/T/R (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "SB" : "EB") +
    //       "): " +
    //       intx1.Major1.LT +
    //       ", " +
    //       intx1.Major1.T +
    //       ", " +
    //       intx1.Major1.RT
    //   );
    //   console.log(
    //     "Z1 Maj2 U-Turn (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "NB" : "WB") +
    //       "): " +
    //       intx1.Major2.UT
    //   );
    //   console.log("Z1 CLV: " + intx1.CLV.toFixed(0));
    //   console.log("Z1 V/C: " + intx1.VC.toFixed(2));
    // }

    // Helper object for Zone 2
    const intx2 = {
      Major2: {
        LT: volumes.Major2.LT,
        T: volumes.Major2.T,
        RT: volumes.Major2.RT,
      },
      Major1: {
        UT: volumes.Minor1.T + volumes.Minor1.LT,
      },
      CLV: -1,
      VC: -1,
    };

    // Determine Z2 Critical Lane Volume and V/C Ratio
    const Z2Maj1LaneVol = errDiv(
      intx2.Major2.LT + intx2.Major2.T + intx2.Major2.RT,
      this.LaneConfig.Z2.Major2.T
    );
    const Z2Maj2ULaneVol = errDiv(
      intx2.Major1.UT,
      this.UTAF,
      this.LaneConfig.Z2.Major1.UT
    );
    intx2.CLV = Z2Maj1LaneVol + Z2Maj2ULaneVol;
    intx2.VC = intx2.CLV / this.CLV_Limit_2;

    // Print statements for debugging
    // if (DEBUG) {
    //   console.log("------- ZONE 2 -------");
    //   console.log(
    //     "Z2 Maj2 L/T/R (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "NB" : "WB") +
    //       "): " +
    //       intx2.Major2.LT +
    //       ", " +
    //       intx2.Major2.T +
    //       ", " +
    //       intx2.Major2.RT
    //   );
    //   console.log(
    //     "Z2 Maj1 U-Turn (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "SB" : "EB") +
    //       "): " +
    //       intx2.Major1.UT
    //   );
    //   console.log("Z2 CLV: " + intx2.CLV.toFixed(0));
    //   console.log("Z2 V/C: " + intx2.VC.toFixed(2));
    // }

    // Helper objects for Zone 3 and Zone 4
    const intx3 = {
      Minor2: {
        LT: volumes.Minor2.LT,
        T: volumes.Minor2.T,
        RT: volumes.Minor2.RT,
        criticalVol: 0,
      },
      Major1: { LT: volumes.Major1.LT, criticalVol: 0 },
      Major2: {
        T: volumes.Major2.T + volumes.Minor1.LT, // Value adjusted below
        RT: 0, // Value set below
      },
      CLV: -1,
      VC: -1,
    };

    const intx4 = {
      Minor1: {
        LT: volumes.Minor1.LT,
        T: volumes.Minor1.T,
        RT: volumes.Minor1.RT,
        criticalVol: 0,
      },
      Major2: { LT: volumes.Major2.LT, criticalVol: 0 },
      Major1: {
        T: volumes.Major1.T + volumes.Minor2.LT, // Value adjusted below
        RT: 0, // Value set below
      },
      CLV: -1,
      VC: -1,
    };

    // Assign/adjust Z4 movment volumes based on lane config inputs
    if (this.LaneConfig.Z3.Major2.RTChan === false) {
      intx3.Major2.RT = volumes.Major2.RT + volumes.Minor1.T;
      if (this.LaneConfig.Z3.Major2.RTShared) {
        // Adjust for shared lane usage
        // NOTE: =ROUND((INDIRECT(CS47 & "R_Master") +INDIRECT(BQ47 & "T_Master")) * ( Z3NBRlanes / ( Z3NBRlanes + 1 ) ),0)
        intx3.Major2.RT = Math.round(
          (intx3.Major2.RT * this.LaneConfig.Z3.Major2.RT) /
            (this.LaneConfig.Z3.Major2.RT + 1)
        );
      }
      intx3.Major2.T =
        intx3.Major2.T +
        Math.round(
          (volumes.Minor1.T + (volumes.Major2.RT - intx3.Major2.RT)) / this.RTAF
        );
    }
    // Determine Z3 Movement Critical Volumes (Major1 and Minor2 only)
    const Z3Maj1LTLaneVol = errDiv(
      intx3.Major1.LT,
      this.LTAF,
      this.LaneConfig.Z3.Major1.LT
    );
    const Z3Maj2ThruLaneVol = errDiv(
      intx3.Major2.T,
      this.LaneConfig.Z3.Major2.T
    );
    const Z3Maj2RTLaneVol = errDiv(
      intx3.Major2.RT,
      this.RTAF,
      this.LaneConfig.Z3.Major2.RT
    );
    intx3.Major1.criticalVol = Math.round(
      Z3Maj1LTLaneVol + Math.max(Z3Maj2ThruLaneVol, Z3Maj2RTLaneVol)
    );
    const Z3Min2LaneVol = errDiv(
      intx3.Minor2.RT + intx3.Minor2.T + intx3.Minor2.LT,
      this.RTAF,
      this.LaneConfig.Z3.Minor2.RT
    );
    intx3.Minor2.criticalVol = Math.round(
      Math.max(Z3Min2LaneVol - Z3Maj1LTLaneVol, 0)
    );
    intx3.CLV = intx3.Major1.criticalVol + intx3.Minor2.criticalVol;
    intx3.VC = intx3.CLV / this.CLV_Limit_2;

    // Assign/adjust Z4 movment volumes based on lane config inputs
    if (this.LaneConfig.Z4.Major1.RTChan === false) {
      intx4.Major1.RT = volumes.Major1.RT + volumes.Minor2.T;
      if (this.LaneConfig.Z4.Major1.RTShared) {
        // Adjust for shared lane usage
        intx4.Major1.RT = Math.round(
          (intx4.Major1.RT * this.LaneConfig.Z4.Major1.RT) /
            (this.LaneConfig.Z4.Major1.RT + 1)
        );
      }
      intx4.Major1.T =
        intx4.Major1.T +
        Math.round(
          (volumes.Minor2.T + (volumes.Major1.RT - intx4.Major1.RT)) / this.RTAF
        );
    }
    // Determine Z4 Movement Critical Volumes (Major1 and Minor2 only)
    const Z4Maj2LTLaneVol = errDiv(
      intx4.Major2.LT,
      this.LTAF,
      this.LaneConfig.Z4.Major2.LT
    );
    const Z4Maj1ThruLaneVol = errDiv(
      intx4.Major1.T,
      this.LaneConfig.Z4.Major1.T
    );
    const Z4Maj1RTLaneVol = errDiv(
      intx4.Major1.RT,
      this.RTAF,
      this.LaneConfig.Z4.Major1.RT
    );
    intx4.Major2.criticalVol = Math.round(
      Z4Maj2LTLaneVol + Math.max(Z4Maj1ThruLaneVol, Z4Maj1RTLaneVol)
    );
    const Z4Min1LaneVol = errDiv(
      intx4.Minor1.RT + intx4.Minor1.T + intx4.Minor1.LT,
      this.RTAF,
      this.LaneConfig.Z4.Minor1.RT
    );
    intx4.Minor1.criticalVol = Math.round(
      Math.max(Z4Min1LaneVol - Z4Maj2LTLaneVol, 0)
    );
    intx4.CLV = intx4.Major2.criticalVol + intx4.Minor1.criticalVol;
    intx4.VC = intx4.CLV / this.CLV_Limit_2;

    // if (DEBUG) {
    //   console.log("------- ZONE 3 -------");
    //   console.log(
    //     "Z3 Min2 L/T/R (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "WB" : "SB") +
    //       "): " +
    //       intx3.Minor2.LT +
    //       ", " +
    //       intx3.Minor2.T +
    //       ", " +
    //       intx3.Minor2.RT
    //   );
    //   console.log(
    //     "Z3 Maj1 L/T/R (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "SB" : "EB") +
    //       "): " +
    //       intx3.Major1.LT +
    //       ", 0, 0"
    //   );
    //   console.log(
    //     "Z3 Maj2 L/T/R (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "NB" : "WB") +
    //       "): 0, " +
    //       intx3.Major2.T +
    //       ", " +
    //       intx3.Major2.RT
    //   );
    //   console.log(
    //     "Z3 Maj1 Critical Vol (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "SB" : "EB") +
    //       "): " +
    //       intx3.Major1.criticalVol.toFixed(0)
    //   );
    //   console.log(
    //     "Z3 Min2 Critical Vol (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "WB" : "SB") +
    //       "): " +
    //       intx3.Minor2.criticalVol.toFixed(0)
    //   );
    //   console.log("Z3 CLV: " + intx3.CLV.toFixed(0));
    //   console.log("Z3 V/C: " + intx3.VC.toFixed(2));
    //   console.log("------- ZONE 4 -------");
    //   console.log(
    //     "Z4 Min1 L/T/R (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "EB" : "NB") +
    //       "): " +
    //       intx4.Minor1.LT +
    //       ", " +
    //       intx4.Minor1.T +
    //       ", " +
    //       intx4.Minor1.RT
    //   );
    //   console.log(
    //     "Z4 Maj2 L/T/R (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "NB" : "WB") +
    //       "): " +
    //       intx4.Major2.LT +
    //       ", 0, 0"
    //   );
    //   console.log(
    //     "Z4 Maj1 L/T/R (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "SB" : "EB") +
    //       "): 0, " +
    //       intx4.Major1.T +
    //       ", " +
    //       intx4.Major1.RT
    //   );
    //   console.log(
    //     "Z4 Maj2 Critical Vol (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "NB" : "WB") +
    //       "): " +
    //       intx4.Major2.criticalVol.toFixed(0)
    //   );
    //   console.log(
    //     "Z4 Min1 Critical Vol (" +
    //       (this.majorStDirection === ORIENTATIONS.NORTH_SOUTH ? "EB" : "NB") +
    //       "): " +
    //       intx4.Minor1.criticalVol.toFixed(0)
    //   );
    //   console.log("Z4 CLV: " + intx4.CLV.toFixed(0));
    //   console.log("Z4 V/C: " + intx4.VC.toFixed(2));
    // }

    this._resultsByZone = {
      Z1: {
        VC: intx1.VC,
        CLV: intx1.CLV,
      },
      Z2: {
        VC: intx2.VC,
        CLV: intx2.CLV,
      },
      Z3: {
        VC: intx3.VC,
        CLV: intx3.CLV,
      },
      Z4: {
        VC: intx4.VC,
        CLV: intx4.CLV,
      },
    };
  }

  getAccommodation() {
    return "0";
  }
}
